import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import React, { useEffect, useMemo, useState } from "react";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { config } from "@/lib/config";
import { lazy, Suspense } from "react";

const Map = lazy(() => import("@/components/main/osm-map"));

export default function PreOrder({
  children,
  title,
  hidden,
}: {
  children?: React.ReactNode;
  title: React.ReactNode | string;
  hidden: string;
}) {
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    fetch(`https://cdn.foxpost.hu/apms.json`)
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        console.log(data.length);
        const locations = data.map((location: any) => {
          return {
            id: location.operator_id,
            lat: location.geolat,
            lng: location.geolng,
            label: location.city,
            data: location,
          };
        });
        setLocations(locations);
      });
  }, []);

  const formSchema = z.object({
    name: z
      .string()
      .min(2, { message: "A név legalább 2 betű legyen" })
      .max(50, { message: "A név maximum 50 betű lehet" }),
    email: z.string().email({ message: "Nem megfelelő e-mail cím" }),
    message: z
      .string()
      .min(0, { message: "Az üzenet hossza legalább 10 betű legyen" })
      .max(2048, { message: "Az üzenet hossza maximum 2048 betű lehet" }),
    box_operator_id: z.string().optional(),
    hidden: z.string().optional(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
      box_operator_id: "",
      hidden: hidden,
    },
  });

  // 2. Define a submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (values && values.name && values.email && values.message) {
      try {
        const submitData = {
          name: values.name,
          email: values.email,
          message: values.message,
          box_operator_id: values.box_operator_id,
          hidden: hidden
            ? values.hidden + " FOXPOST=" + values.box_operator_id
            : "",
        };

        const res = await fetch(`${config.basePath}/api/`, {
          method: "POST",
          body: JSON.stringify(submitData),
          headers: {
            "content-type": "application/json",
          },
        });
        if (res.ok) {
        } else {
          console.error("Oops! Something is wrong.");
        }
      } catch (error) {
        console.log(error);
      }
      toast.success("Az üzenet elküldve");
      form.reset();
    }
    console.log(values);
  }

  const [location, setLocation] = useState<any>(null);

  const onMapSelection = (location: any) => {
    setLocation(location);
    form.setValue(
      "box_operator_id",
      location.data.operator_id.toString().toUpperCase(),
    );
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-full space-y-8 p-8"
      >
        {children}
        <h1 className="w-full text-center text-2xl font-bold">
          {title ? title : "Kapcsolat"}
        </h1>
        <FormField
          control={form.control}
          name="hidden"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input type="hidden" placeholder="" {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-2xl font-bold">Név</FormLabel>
              <FormControl>
                <Input placeholder="Név" {...field} />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-2xl font-bold">E-mail</FormLabel>
              <FormControl>
                <Input placeholder="E-mail cím" {...field} />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-2xl font-bold">
                Számlázási adatok, darabszám és opciók megadása (számlázási név
                és cím)
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Üzenet"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="box_operator_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-2xl font-bold">
                FoxPost automata száma
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Válaszd ki az automatát a térképről"
                  {...field}
                  readOnly={true}
                />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div>
          <div>
            <div className="mb-8 mt-4">
              <div>
                Válassz ki egy FoxPost csomagautomatát az alábbi térképen!
                <br />
                Kattints a megfelelő automatához tartozó ikonra, majd a
                &quot;Kiválasztom&quot; gombra!
              </div>
              <div>
                <div className="mb-2 mt-2 font-bold text-red-900">
                  Az üzenet mezőben kérlek add meg a mobil telefonszámod is,
                  mert a csomag feladásához ez is szükséges! (Az értesítő
                  SMS-eket erre a számra fogod megkapni)
                </div>
                Amennyiben más módon szeretnéd megkapni a csomagod (MPL), kérlek
                írj üzenetet!
                <br /> Az előrendelés elküldése után, a feldolgozás során kapod
                meg majd a díjbekérőt, amin a fizetési adatok szerepelnek.
              </div>
            </div>
            {location !== null && (
              <div className="border-1 mb-4 border-amber-300">
                <div className="mb-2 font-bold">
                  A kiválasztott automata adatai
                </div>
                <div className="flex">
                  <div className="mr-2 font-bold">
                    {location.data.operator_id.toString().toUpperCase()}
                  </div>
                  <div className="font-bold">{location.data.name}</div>
                </div>
                <div>{location.data.address}</div>
                <div className="text-sm italic">{location.data.findme}</div>
              </div>
            )}
          </div>
          <Suspense fallback={<div>Betöltés...</div>}>
            <Map locations={locations} onMapSelection={onMapSelection} />
          </Suspense>
        </div>

        <Button
          type="submit"
          className="mx-auto w-full bg-red-900 text-2xl font-bold hover:bg-red-600"
        >
          Küldés
        </Button>
      </form>
    </Form>
  );
}
